import useWindowScroll from '@/lib/hooks/useWindowScroll';
import useStore from '@/stores/useStore';
import { FormActionType } from '@/types/shared';
import { useEffect } from 'react';

type Props = {
  hasGlobalBanner?: boolean;
};

const HeaderScrollThreshold = 50;

const BannerScrollThreshold = 172;

export const useHeader = ({ hasGlobalBanner }: Props) => {
  const [scrollY] = useWindowScroll();

  const scrollThreshold = hasGlobalBanner ? HeaderScrollThreshold + BannerScrollThreshold : HeaderScrollThreshold;

  const {
    isHeaderScrolled,
    isMobileMenuOpen,
    activeDesktopHeaderMenuIndex,
    setHeaderScrolled,
    setMobileMenuOpen,
    setDesktopHeaderMenuIndex,
    setDemoModalOpen,
    setTrialModalOpen,
  } = useStore();

  // Change header scrolled in global store
  useEffect(() => {
    if (scrollY >= scrollThreshold && !isHeaderScrolled) {
      setHeaderScrolled(true);
    } else if (scrollY < scrollThreshold && isHeaderScrolled) {
      setHeaderScrolled(false);
    }
  }, [scrollY, isHeaderScrolled]);

  const onToggleMobileMenu = () => setMobileMenuOpen(!isMobileMenuOpen);

  const onCloseAllMenus = () => {
    setMobileMenuOpen(false);
    setDesktopHeaderMenuIndex(null);
  };

  const onMouseLeaveHeader = () => {
    // Don't effect mobile menu
    if (isMobileMenuOpen) return;

    setMobileMenuOpen(false);
    setDesktopHeaderMenuIndex(null);
  };

  const onClickMenuDrawer = (index: number) => () => {
    if (index === activeDesktopHeaderMenuIndex) {
      setDesktopHeaderMenuIndex(null);
    } else {
      setDesktopHeaderMenuIndex(index);
    }

    setMobileMenuOpen(false);
  };

  const onMouseEnterMenuDrawer = (index: number) => () => {
    setDesktopHeaderMenuIndex(index);
    setMobileMenuOpen(false);
  };

  const onClickMenuButton = (actionType: FormActionType | undefined) => {
    if (actionType == null) return;

    const actions = {
      trial: () => setTrialModalOpen(true),
      demo: () => setDemoModalOpen(true),
      url: () => {},
    } as const satisfies Record<FormActionType, () => void>;

    const action = actions[actionType];

    if (!action) return;

    action();
  };

  return {
    onToggleMobileMenu,
    onCloseAllMenus,
    onMouseLeaveHeader,
    onClickMenuDrawer,
    onMouseEnterMenuDrawer,
    onClickMenuButton,
    activeDesktopHeaderMenuIndex,
    isMobileMenuOpen,
    isHeaderScrolled,
  };
};
